import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import InfoTooltip from "Shared/InfoTooltip";
import LoadingBar from "Shared/LoadingBar";
import { ApplicationStore } from "src/store";
import {
    deleteConditionalRebatesRequest,
    getConditionalRebatesRequest,
    putConditionalRebatesRequest,
} from "src/store/actions/conditionalRebates";
import find from "lodash/find";
import debounce from "lodash.debounce";
import { ConditionalRebatesStatus } from "src/store/reducers/conditionalRebates";
import { ThemeContext } from "styled-components";
import { ConditionalRebate } from "Types/state";

interface Props {
    vin: string;
}

export const ConditionalRebates = ({
    vin,
    shouldFetch,
    isLoading,
    available,
    selected,
    dealership,
    dealUuid,
    fetch,
    select,
    unselect,
}: Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
    const { t } = useTranslation();

    const themeContext = useContext(ThemeContext);

    const [viewMore, setViewMore] = useState(false);

    useEffect(() => {
        if (shouldFetch) {
            fetch(vin, dealUuid);
        }
    }, [shouldFetch, vin, fetch, dealUuid]);

    const handleClick = useCallback(
        debounce(
            (rebate: ConditionalRebate, rebates: ConditionalRebate[]) => {
                find(rebates, rebate) ? unselect(rebate, dealUuid) : select(rebate, dealUuid);
            },
            200,
            { leading: true, trailing: false, maxWait: 400 }
        ),
        [dealUuid]
    );

    if (available.length === 0 && isLoading) {
        return (
            <div className={"conditional_rebates_loading"}>
                <div>
                    <LoadingBar />
                </div>
            </div>
        );
    }

    if (available.length === 0 && !isLoading) {
        return (
            <div className={"conditional_rebates_none"}>
                <div>{t("ConditionalRebates.None")}</div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className={"conditional_rebates"}>
                {available?.slice(0, viewMore ? available?.length : 4).map((rebate) => (
                    <div
                        className={"conditional_rebate_row"}
                        key={`${rebate.id}${rebate.subcategory ? rebate.subcategory : ""}`}
                    >
                        {find(selected, rebate) ? (
                            <div
                                className="conditional_rebate conditional_rebate_selected"
                                style={{
                                    borderColor: themeContext.palette.primary.main,
                                    backgroundColor: "#fff",
                                }}
                                onClick={() => handleClick(rebate, selected)}
                            >
                                {rebate.name}
                            </div>
                        ) : (
                            <div
                                className="conditional_rebate conditional_rebate_unselected"
                                onClick={() => handleClick(rebate, selected)}
                            >
                                {rebate.name}
                            </div>
                        )}

                        <InfoTooltip
                            className={"conditional_rebate_tooltip"}
                            text={
                                rebate.description ??
                                t("ConditionalRebates.ToolTip", {
                                    dealership,
                                })
                            }
                        />
                    </div>
                ))}
            </div>

            {available.length > 4 && (
                <div
                    style={{
                        color: themeContext.palette.primary.main,
                    }}
                    className={"conditional_rebates_show_more"}
                    onClick={() => setViewMore(!viewMore)}
                >
                    {viewMore ? `View less` : `View more`}
                </div>
            )}
        </React.Fragment>
    );
};

const mapStateToProps = (state: ApplicationStore, { vin }: Props) => ({
    shouldFetch: state.conditionalRebates.dataDriven.available?.[vin] === undefined,
    isLoading: state.conditionalRebates.status === ConditionalRebatesStatus.Loading,
    available: state.conditionalRebates.dataDriven.available?.[vin] ?? [],
    selected: state.conditionalRebates.dataDriven.selected,
    dealership: state.account.name,
    dealUuid: state.deal.uuid as string,
});

const mapDispatchToProps = {
    fetch: getConditionalRebatesRequest,
    select: putConditionalRebatesRequest,
    unselect: deleteConditionalRebatesRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConditionalRebates);
